<script lang="ts">
const __DsBlock = {
	name: 'TranslationKey',
	type: 'input',
	category: 'input',
	hidden: true,
}
</script>
<script lang="ts" setup>
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { InputProps } from '#layers/ui/public';
import { reactivePick } from '@vueuse/core';
import { tv } from 'tailwind-variants';
import { computed, ref, watch } from 'vue';
import nuxtUiInputTheme from '#build/ui/input';
import { isNil } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import { usePrimitiveFormField } from '#layers/forms/public';
import { isTranslationPlaceholder, useTranslationUtils } from '#layers/i18n/public';
import { useDisabledNotice, useFormField, useMergedTheme } from '#layers/ui/public';

interface Props extends InputBlockProps, Pick<InputProps<any>, 'variant' | 'size' | 'block'> {}

const props = withDefaults(defineProps<Props>(), {
	block: true,
});

const model = defineModel<string>();

;

const forwarded = reactivePick(props, 'variant', 'size', 'block');
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
// `DInput` picks these up on its own; the read-only frame is not a Nuxt UI input, so it has to
// carry the field's describedby/invalid wiring itself. `forwardContext` re-provides the context
// Nuxt UI's `useFormField` unconditionally clears, which would otherwise strip the plain-mode
// `DInput` of its aria wiring and blur/input validation triggers.
const { id: formFieldId, ariaAttrs } = useFormField<Props>(props, { forwardContext: true });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const { translateString } = useTranslationUtils();

/**
 * Which of the two modes this field renders in.
 *
 * A mount-time check is too early — rstore hands out `undefined` before the record arrives — and a
 * fully reactive check is too late: it would turn a plain field into a locked badge the moment
 * someone types `$t:` into it. So plain mode sticks, while badge mode stays free to re-evaluate:
 * it is read-only, so any value it sees change came from outside, not from an edit.
 */
const showTranslatedValue = ref<boolean>();

watch(modelValue, (value) => {
	if (showTranslatedValue.value === false || isNil(value)) return;
	showTranslatedValue.value = isTranslationPlaceholder(value);
}, { immediate: true });

const translatedValue = computed(() => translateString(modelValue.value));

const inputTheme = useMergedTheme({ input: nuxtUiInputTheme });
const theme = computed(() => tv(inputTheme)({
	variant: props.variant,
	size: props.size,
}));
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<!-- A translation key is owned by the system, so it is rendered as a read-only label that
		keeps the shape of the input it replaces. -->
		<div
			v-if="showTranslatedValue"
			:id="formFieldId"
			role="textbox"
			aria-readonly="true"
			:aria-disabled="disabled || undefined"
			:tabindex="disabled ? -1 : 0"
			:class="theme.root({ class: [block ? 'w-full' : '', disabled ? 'opacity-50' : ''] })"
			v-bind="ariaAttrs"
		>
			<span :class="theme.base()">
				<DBadge variant="soft" size="xs" class="text-xs leading-none" :label="translatedValue" />
			</span>
		</div>

		<DInput
			v-else
			v-bind="forwarded"
			v-model="modelValue"
			:readonly
			:aria-readonly="readonly"
			:disabled
			class="min-w-0"
		/>
	</DTooltip>
</template>
